// Owns selection behavior for built-in and user-saved screener views.
import type { SavedViewRecord } from "../../lib/saved-views-api";
import { BUILT_IN_VIEWS } from "../../lib/screener-state";
import type { BuiltInViewId } from "../../lib/types";

interface ViewSelectorProps {
  builtInViewId: BuiltInViewId;
  selectedSavedView: SavedViewRecord | null;
  savedViews: readonly SavedViewRecord[];
  disabled: boolean;
  onSelectBuiltInView: (id: BuiltInViewId) => void;
  onSelectSavedView: (view: SavedViewRecord) => void;
}

/** Renders the current view selector and routes valid selections to existing callbacks. */
export function ViewSelector({
  builtInViewId,
  selectedSavedView,
  savedViews,
  disabled,
  onSelectBuiltInView,
  onSelectSavedView
}: ViewSelectorProps) {
  const selectedViewValue = selectedSavedView
    ? `saved:${selectedSavedView.id}`
    : `builtin:${builtInViewId}`;

  const selectView = (value: string) => {
    if (value.startsWith("builtin:")) {
      const id = value.slice("builtin:".length) as BuiltInViewId;
      if (Object.prototype.hasOwnProperty.call(BUILT_IN_VIEWS, id)) onSelectBuiltInView(id);
      return;
    }
    if (value.startsWith("saved:")) {
      const id = value.slice("saved:".length);
      const view = savedViews.find((candidate) => candidate.id === id);
      if (view) onSelectSavedView(view);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <select
        aria-label="View"
        className="h-8 max-w-44 rounded-md border border-input bg-surface-elevated px-2 text-xs font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        value={selectedViewValue}
        disabled={disabled}
        onChange={(event) => selectView(event.target.value)}
      >
        <optgroup label="Built-in views">
          {Object.values(BUILT_IN_VIEWS).map((view) => (
            <option key={view.id} value={`builtin:${view.id}`}>
              {view.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Your saved views">
          {savedViews.map((view) => (
            <option key={view.id} value={`saved:${view.id}`}>
              {view.name}
            </option>
          ))}
        </optgroup>
      </select>
      <span className="hidden whitespace-nowrap text-[11px] text-muted-foreground xl:inline">
        {selectedSavedView ? "User-saved view" : "Built-in presets are immutable."}
      </span>
    </div>
  );
}
